/**
 * Additional Product Section Component for Extended Product Information
 * 
 * A comprehensive component that manages expandable sections containing detailed
 * product information, technical specifications, geometry data, and downloadable
 * resources. Features dynamic content rendering and interactive state management.
 * 
 * Core Features:
 * - Expandable/collapsible content sections with smooth animations
 * - Dynamic geometry type switching (race/endurance for road bikes)
 * - Rich content rendering via Portable Text integration
 * - File download management with document links
 * - Event-driven section expansion (external triggers)
 * - Responsive design with mobile-optimized layouts
 * 
 * Content Types Supported:
 * - Overview: Product description and key features
 * - Geometry: Technical bike geometry with switchable configurations
 * - Certifications: Safety and quality certifications display
 * - Features: Detailed feature breakdowns with rich content
 * - Pictures: Image galleries and visual content
 * - Downloads: User manuals, specifications, and documentation
 * 
 * Interactive Features:
 * - Section state persistence during navigation
 * - External expansion triggers via custom events
 * - Dynamic geometry visualization switching
 * - Progressive content loading with Suspense boundaries
 * 
 * Technical Implementation:
 * - React hooks for state management (useState, useEffect)
 * - Dynamic imports for performance optimization
 * - Sanity CMS integration for content and images
 * - Custom event system for cross-component communication
 * 
 * Accessibility:
 * - Keyboard navigation for expand/collapse controls
 * - Screen reader compatible section headings
 * - Focus management for dynamic content
 * - Semantic HTML structure with proper ARIA attributes
 * 
 * @param {Array} detailSections - Array of content sections from Sanity CMS
 * @param {boolean} showDownloads - Whether to display download links (default: true)
 * 
 * @description Advanced expandable content manager for detailed product information
 */

import { useState, useEffect, Suspense } from 'react';
import Image from 'next/image';
import { Plus, Minus } from 'lucide-react';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { DynamicPortableTextWithComponents } from '../../utils/dynamicImports';
import portableTextComponents from '../ui/PortableTextComponents';
import { useTranslation } from '../../src/hooks/useTranslation';

// Component to display category features - now using server-side data
const CategoryFeaturesDisplay = ({ section, category }) => {
  // Use the resolved features from server-side data
  const features = section.resolvedFeatures || [];

  if (!features.length) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-gray-600">No features selected for this product.</p>
        <p className="text-sm text-gray-500 mt-1">
          Features can be selected from legacy options or the central features library.
        </p>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 px-6">
      {features.map((feature, i) => (
        <li key={feature.id || feature._key || feature.title || `feature-${i}`} className="flex flex-col space-y-3">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium text-lg">{feature.title}</span>
          </div>
          {feature.body && feature.body.length > 0 && (
            <div className="ml-8 text-gray-600">
              <Suspense fallback={<div className="animate-pulse h-4 bg-gray-200 rounded"></div>}>
                <DynamicPortableTextWithComponents 
                  value={feature.body} 
                  components={portableTextComponents} 
                />
              </Suspense>
            </div>
          )}
          {feature.image && (
            <div className="ml-8 mt-3">
              <Image
                src={urlForImage(feature.image)}
                alt={feature.title || 'Feature image'}
                loader={sanityLoader}
                width={300}
                height={200}
                className="rounded-lg"
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

const AdditionalProductSection = ({ 
  detailSections = [],
  showDownloads = true,
  category = 'Road'
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({});
  const [geometryType, setGeometryType] = useState('race');

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Function to handle geometry type selection
  const handleGeometryTypeChange = (type) => {
    setGeometryType(type);
  };

  // Listen for the expandOverview event
  useEffect(() => {
    const handleExpandOverview = () => {
      // Safely check if section.contentType is a string and equals 'overview'
      const overviewIndex = detailSections.findIndex(section => 
        typeof section.contentType === 'string' && section.contentType === 'overview'
      );
      
      if (overviewIndex !== -1) {
        setExpandedSections(prev => ({
          ...prev,
          [overviewIndex]: true
        }));
      }
    };

    document.addEventListener('expandOverview', handleExpandOverview);
    return () => document.removeEventListener('expandOverview', handleExpandOverview);
  }, [detailSections]);
  
  // Initialize geometry type from product settings if available
  useEffect(() => {
    // Safely check if section.contentType is a string before using includes()
    const geometrySection = detailSections.find(section => 
      typeof section.contentType === 'string' && 
      section.contentType === 'geometry' && 
      typeof section.category === 'string' &&
      section.category === 'Road' &&
      section.geometrySettings?.geometryType
    );
    
    if (geometrySection) {
      setGeometryType(geometrySection.geometrySettings.geometryType);
    }
  }, [detailSections]);

  if (!detailSections || detailSections.length === 0) {
    return null;
  }

  // Common table header for all geometry tables
  const renderGeometryTableHeader = (section) => {
    // Based on bike category, use the appropriate headers
    if (section.category === 'MTB') {
      return (
        <thead className="bg-gray-800 text-white">
          <tr>
            <th className="py-3 px-4 text-left">Dimensions (mm)</th>
            <th className="py-3 px-4 text-center">Code</th>
            <th className="py-3 px-4 text-center">YGGY XS</th>
            <th className="py-3 px-4 text-center">YGGY S</th>
            <th className="py-3 px-4 text-center">YGGY M</th>
            <th className="py-3 px-4 text-center">YGGY L</th>
            <th className="py-3 px-4 text-center">YGGY XL</th>
            <th className="py-3 px-4 text-center">YGGY XXL</th>
          </tr>
        </thead>
      );
    } else if (section.category === 'Gravel') {
      return (
        <thead className="bg-gray-800 text-white">
          <tr>
            <th className="py-3 px-4 text-left">Dimensions (mm)</th>
            <th className="py-3 px-4 text-center">Code</th>
            <th className="py-3 px-4 text-center">BRN XS</th>
            <th className="py-3 px-4 text-center">BRN S</th>
            <th className="py-3 px-4 text-center">BRN M</th>
            <th className="py-3 px-4 text-center">BRN L</th>
            <th className="py-3 px-4 text-center">BRN XL</th>
            <th className="py-3 px-4 text-center">BRN XXL</th>
          </tr>
        </thead>
      );
    } else {
      // Road bikes - use GRC or GED prefix based on geometry type
      const prefix = geometryType === 'endurance' ? 'GED' : 'GRC';
      
      return (
        <thead className="bg-gray-800 text-white">
          <tr>
            <th className="py-3 px-4 text-left">Dimensions (mm)</th>
            <th className="py-3 px-4 text-center">Code</th>
            <th className="py-3 px-4 text-center">{prefix} XS</th>
            <th className="py-3 px-4 text-center">{prefix} S</th>
            <th className="py-3 px-4 text-center">{prefix} M</th>
            <th className="py-3 px-4 text-center">{prefix} L</th>
            <th className="py-3 px-4 text-center">{prefix} XL</th>
            <th className="py-3 px-4 text-center">{prefix} XXL</th>
          </tr>
        </thead>
      );
    }
  };

  // Common function to render a data row with consistent styling
  const renderGeometryRow = (row, i, fieldMap) => (
    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
      <td className="py-3 px-4">{row.dimension}</td>
      <td className="py-3 px-4 text-center">{row.code}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.xs]}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.s]}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.m]}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.l]}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.xl]}</td>
      <td className="py-3 px-3 text-center">{row[fieldMap.xxl]}</td>
    </tr>
  );

  // Helper function to safely check contentType
  const isContentType = (section, type) => {
    return typeof section.contentType === 'string' && section.contentType === type;
  };

  return (
    <div id="additional-product-sections" className="space-y-12 py-4 max-w-6xl mx-auto">
      {detailSections.filter(section => section.enabled !== false).map((section, index) => {
        const isOverview = isContentType(section, 'overview');
        const isExpanded = isOverview || expandedSections[index];
        
        return (
          <div 
            key={section._key || index}
            id={`${isOverview ? 'overview-section' : `section-${index}`}`}
            className={`border-b border-gray-200 pb-4 last:border-b-0 ${
              isOverview ? 'bg-white ' : ''
            }`}
          >
            <div 
              className="flex items-center justify-between cursor-pointer px-6"
              onClick={() => toggleSection(index)}
            >
              <h3 className={`font-bold ${
                isOverview ? 'text-3xl' : 'text-xl'
              }`}>
                {section.sectionTitle}
              </h3>
              <button 
                className={`p-1 rounded-full transition-colors ${
                  expandedSections[index] 
                    ? 'hover:bg-gray-100' 
                    : 'hover:bg-gray-100'
                }`}
                aria-label={expandedSections[index] ? 'Collapse section' : 'Expand section'}
              >
                {expandedSections[index] ? (
                  <Minus className="h-6 w-6 text-gray-800" />
                ) : (
                  <Plus className="h-6 w-6 text-gray-800" />
                )}
              </button>
            </div>
          
            <div className={`mt-8 transition-all duration-300 ease-in-out ${
              expandedSections[index] ? 'opacity-100 pb-8' : 'opacity-0 h-0 overflow-hidden'
            }`}>
              {isContentType(section, 'overview') && (
                <div className="prose max-w-none text-black text-lg px-6 text-justify">
                  {Array.isArray(section.overview) && (
                    <Suspense fallback={<div className="animate-pulse h-8 bg-gray-200 rounded"></div>}>
                      <DynamicPortableTextWithComponents 
                        value={section.overview}
                        components={portableTextComponents}
                      />
                    </Suspense>
                  )}
                </div>
              )}
              
              {isContentType(section, 'specs') && (
                <div className="space-y-8 px-6">
                  {section.specGroups?.map((group, groupIndex) => (
                    <div key={group._key || groupIndex} className="bg-gray-100 rounded-lg p-6">
                      <h4 className="text-m font-semibold mb-6 text-orange-500">{group.groupTitle}</h4>
                      <div className="space-y-4">
                        {group.specs?.map((spec, i) => (
                          <div key={spec._key || i} className="flex flex-row border-b border-gray-200 pb-3">
                            <span className="font-medium text-gray-800 w-1/3">{spec.key}</span>
                            <span className="text-gray-600 flex-1">{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {isContentType(section, 'features') && (
                <CategoryFeaturesDisplay 
                  section={section} 
                  category={category} 
                />
              )}

              {isContentType(section, 'geometry') && section.geometrySettings && section.geometrySettings.showGeometry && (
                <div className="overflow-x-auto mx-4 md:mx-8 text-sm">
                  {/* For Road category with Race/Endurance options */}
                  {section.category === 'Road' && (
                    <>
                      {/* Geometry Type Selector for Road bikes */}
                      <div className="flex justify-center mb-4 pt-4">
                        <div className="inline-flex bg-gray-200 rounded-lg p-1">
                          <button 
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              geometryType === 'race' 
                                ? 'bg-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => handleGeometryTypeChange('race')}
                          >
                            Race Geometry
                          </button>
                          <button 
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              geometryType === 'endurance' 
                                ? 'bg-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => handleGeometryTypeChange('endurance')}
                          >
                            Endurance Geometry
                          </button>
                        </div>
                      </div>

                      {/* Render appropriate geometry table based on selection */}
                      <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg text-sm">
                        {renderGeometryTableHeader(section)}
                        <tbody className="divide-y divide-gray-200">
                          {geometryType === 'endurance' 
                            ? section.categoryGeometry?.enduranceGeometry?.rows?.map((row, i) => 
                              renderGeometryRow(row, i, {
                                xs: 'gedxs',
                                s: 'geds',
                                m: 'gedm',
                                l: 'gedl',
                                xl: 'gedxl',
                                xxl: 'gedxxl'
                              })
                            )
                            : section.categoryGeometry?.raceGeometry?.rows?.map((row, i) => 
                              renderGeometryRow(row, i, {
                                xs: 'grcxs',
                                s: 'grcs',
                                m: 'grcm',
                                l: 'grcl',
                                xl: 'grcxl',
                                xxl: 'grcxxl'
                              })
                            )
                          }
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* For Gravel and MTB categories with a single geometry table */}
                  {(section.category === 'Gravel' || section.category === 'MTB') && (
                    <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg text-sm">
                      {renderGeometryTableHeader(section)}
                      <tbody className="divide-y divide-gray-200">
                        {section.category === 'Gravel' 
                          ? section.categoryGeometry?.geometryTable?.rows?.map((row, i) => 
                            renderGeometryRow(row, i, {
                              xs: 'brgxs',
                              s: 'brgs',
                              m: 'brgm',
                              l: 'brgl',
                              xl: 'brgxl',
                              xxl: 'brgxxl'
                            })
                          )
                          : section.categoryGeometry?.geometryTable?.rows?.map((row, i) => 
                            renderGeometryRow(row, i, {
                              xs: 'xs',
                              s: 's',
                              m: 'm',
                              l: 'l',
                              xl: 'xl',
                              xxl: 'xxl'
                            })
                          )
                        }
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Downloads Section - Always Last */}
      {showDownloads && (
        <div className="border-b border-gray-200 pb-4 last:border-b-0">
          <div 
            className="flex items-center justify-between cursor-pointer px-6"
            onClick={() => toggleSection('downloads')}
          >
            <h3 className="text-xl font-bold">{t('product.additional_product_section.downloads')}</h3>
            <button 
              className={`p-1 rounded-full transition-colors ${
                expandedSections['downloads']
                  ? 'hover:bg-gray-100' 
                  : 'hover:bg-gray-100'
              }`}
              aria-label={expandedSections['downloads'] ? 'Collapse section' : 'Expand section'}
            >
              {expandedSections['downloads'] ? (
                <Minus className="h-6 w-6 text-gray-800" />
              ) : (
                <Plus className="h-6 w-6 text-gray-800" />
              )}
            </button>
          </div>

          <div className={`mt-8 transition-all duration-300 ease-in-out ${
            expandedSections['downloads'] ? 'opacity-100 pb-8' : 'opacity-0 h-0 overflow-hidden'
          }`}>
            <ul className="space-y-4 mx-8">
              <li>
                <a 
                  href="/documents/MU-001 User manual WOODALPS wooden bikes_en_250327.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <svg className="h-6 w-6 text-orange-500 mr-3 group-hover:text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1">
                    <span className="font-medium block group-hover:text-orange-500 transition-colors">WOODALPS Owner's Manual (English PDF)</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="/documents/MU-001 Manuel utilisateur vélos WOODALPS_fr_250401.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <svg className="h-6 w-6 text-orange-500 mr-3 group-hover:text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1">
                    <span className="font-medium block group-hover:text-orange-500 transition-colors">Manuel d'utilisateur WOODALPS (Français PDF)</span>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalProductSection; 